import {FeatureCollection} from 'geojson';
import isDefinedType from 'is-defined-type';
import {combineEpics, Epic, StateObservable} from 'redux-observable';
import {EMPTY, of} from 'rxjs';
import {filter, map, mergeMap} from 'rxjs/operators';
import {isActionOf} from 'typesafe-actions';
import {LayerDTO} from '../../../interfaces/LayerDTO';
import {SearchFeature} from '../../../interfaces/SearchFeature';
import {HttpSnapshot, StreamableSnapshot, VectorSnapshotEvent} from '../../../models';
import {RootState} from '../../../reducer';
import {
    mapSceneDataClearFilteredDataForDataSource,
    mapSceneDataClearMapFilteredData,
    mapSceneDataLoadDataSource,
    mapSceneDataSourceUpdateMissed,
    mapSceneDataUpdateBaseDataSource,
    mapSceneDataUpdateDataSource,
    mapSceneDataUpdateDataSourceFromWebsocket,
    mapSceneDataUpdateGeoJSONDataSource,
    mapSceneDataUpdateSearchFeatures,
    mapSceneDataUpdateVectorLayerDataMapSource,
} from '../../../scenes/MapScene/actions/reducers/data';
import {
    mapSceneMapLayersActivateDataSource,
    mapSceneMapLayersUpdateMapLayerConfiguration,
} from '../../../scenes/MapScene/actions/reducers/mapLayers';
import {loadSourceData} from '../../../services/ViewerApiService';
import {
    handleMetadataReceivedForDataSource,
    viewerApiServiceReceivedSourceData,
} from '../../../services/ViewerApiService/actions/sourceDataData';
import {
    viewerWebsocketServiceMapSourceUpdateMessage,
    viewerWebsocketServiceResetEventMessage,
} from '../../../services/ViewerWebsocketService/actions';
import {DataSourceConfig, dataSourceFactory} from '../../../services/DataSourceService';
import {DataSourceId} from '../../../interfaces/DataSource';
import {GeoJsonSnapshotEvent} from '../../../generated/DataStreamerApi';

export interface LayerUpdateData {
    previousIdentifier: number;
    identifier: number;
}

// Verify the snapshot is an HttpSnapshot (snapshot sent from viewer-common to the PHP backend)
const isHttpSnapshot = (snapshot: LayerDTO | GeoJsonSnapshotEvent | VectorSnapshotEvent): snapshot is HttpSnapshot => {
    return (
        snapshot?.data &&
        typeof snapshot.data === 'object' &&
        'type' in snapshot.data &&
        snapshot.data.type === 'GeoJsonSnapshotEvent' &&
        snapshot.data.data &&
        Array.isArray(snapshot.data.data.features)
    );
};

const delegateMapLayerDataStorageOnReceivedSourceData: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(viewerApiServiceReceivedSourceData)),
        map(({payload: {data: sourceData, mapSource, dataType}}) => {
            if (dataType === 'base') {
                // if mapSource is streamable, then the response is StreamableSnapshot
                if (mapSource.streamable) {
                    const snapshot: StreamableSnapshot = sourceData as StreamableSnapshot;
                    return mapSceneDataUpdateBaseDataSource(
                        {
                            ...snapshot.data,
                            identifier: snapshot.timestamp,
                        },
                        mapSource,
                    );
                } else if (isHttpSnapshot(sourceData)) {
                    return mapSceneDataUpdateBaseDataSource(
                        {
                            ...sourceData.data.data,
                            identifier: sourceData.data.timestamp,
                        },
                        mapSource,
                    );
                }

                // The response is a snapshot created in the PHP backend
                const data = sourceData as LayerDTO;
                return mapSceneDataUpdateBaseDataSource(
                    data.data as FeatureCollection & {
                        identifier: number;
                    },
                    mapSource,
                );
            } else if (dataType === 'data') {
                if ((sourceData.data as VectorSnapshotEvent).type === 'VectorSnapshotEvent') {
                    return mapSceneDataUpdateVectorLayerDataMapSource(
                        ((sourceData as LayerDTO).data as VectorSnapshotEvent).data, mapSource);
                }
                return mapSceneDataUpdateVectorLayerDataMapSource((sourceData as LayerDTO).data, mapSource);
            } else if (dataType === 'update') {
                if (
                    mapSource.sourceUpdateIdentifier !== null &&
                    mapSource.sourceUpdateIdentifier !== (sourceData.data as LayerUpdateData).previousIdentifier
                ) {
                    return mapSceneDataSourceUpdateMissed(mapSource);
                }

                return mapSceneDataUpdateGeoJSONDataSource(sourceData.data, mapSource);
            } else {
                return EMPTY;
            }
        }),
    );

const buildSearchObjectsOnReceivedSourceBaseData: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(mapSceneDataUpdateBaseDataSource)),
        map(({payload: {dataSource, data}}) => {
            const searchFeatures: SearchFeature[] = [];

            if (dataSource.dataLoadMethod === 'GeoJSON') {
                data.features.forEach((feature) => {
                    const displayValue = isDefinedType(feature, dataSource.searchProperty, ['string', 'value']);
                    let latitude: number | undefined;
                    let longitude: number | undefined;
                    if (feature.geometry.type === 'Point') {
                        latitude = feature.geometry.coordinates[1];
                        longitude = feature.geometry.coordinates[0];
                    } else if (feature.geometry.type === 'LineString') {
                        latitude = feature.geometry.coordinates[0][1];
                        longitude = feature.geometry.coordinates[0][0];
                    }

                    const searchFeature: SearchFeature = {
                        coordinates: {
                            latitude: latitude,
                            longitude: longitude,
                        },
                        displayValue: displayValue,
                        id: feature.properties?.id || '',
                    };
                    searchFeatures.push(searchFeature);
                });
            }

            return mapSceneDataUpdateSearchFeatures(dataSource.id, searchFeatures);
        }),
    );

const clearFilteredDataForMapSourceOnClearMapFilteredData: Epic = (action$, state$: StateObservable<RootState>) =>
    action$.pipe(
        filter(isActionOf(mapSceneDataClearMapFilteredData)),
        mergeMap(() => {
            const mapSourceIDsWithSearchData: string[] = [];

            for (const sourcesKey in state$.value.mapScene.data.sources) {
                if (!state$.value.mapScene.data.sources.hasOwnProperty(sourcesKey)) {
                    continue;
                }

                const source = state$.value.mapScene.data.sources[sourcesKey];

                if (source.isActive || source.layerFilteredMapData) {
                    mapSourceIDsWithSearchData.push(source.id);
                }
            }

            return of(...mapSourceIDsWithSearchData).pipe(
                map((mapSourceID: string) => mapSceneDataClearFilteredDataForDataSource(mapSourceID)),
            );
        }),
    );

const loadBaseDataOnActivateMapSource: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(mapSceneMapLayersActivateDataSource)),
        filter((action) => action.payload.mapSource.loadBaseData),
        map((action) => mapSceneDataLoadDataSource(action.payload.mapSource)),
    );

const loadSourceDataOnLoadDataSource: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(mapSceneDataLoadDataSource)),
        mergeMap((action) =>
            loadSourceData(
                action.payload.dataSource,
                action.payload.dataSource.dataLoadMethod === 'GeoJSON' ? 'base' : 'data',
            ),
        ),
    );

const activateDataSourceConfigOnLoadDataSource: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(mapSceneDataLoadDataSource)),
        map(action => dataSourceFactory(action.payload.dataSource.id)),
        filter((config): config is DataSourceConfig => config !== null),
        mergeMap(datasourceConfig => {
            return datasourceConfig.onActivate();
        }),
    );

const loadMetadataOnReceivedDatasourceConfiguration: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(mapSceneMapLayersUpdateMapLayerConfiguration)),
        map(action => Object.keys(action.payload.dataSources)),
        map(dataSources => dataSources.map(dataSourceId => dataSourceFactory(dataSourceId as DataSourceId))),
        mergeMap(datasourceConfigs => datasourceConfigs.filter(datasourceConfig => datasourceConfig !== null) as DataSourceConfig[]),
        mergeMap(datasourceConfig => {
            if (datasourceConfig.loadMetadata) {
                return datasourceConfig.loadMetadata();
            }

            return EMPTY;
        }),
    );

const loadSourceDataOnUpdateDataSource: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(mapSceneDataUpdateDataSource)),
        mergeMap((action) =>
            loadSourceData(
                action.payload.dataSource,
                action.payload.dataSource.dataLoadMethod === 'VectorTiles' ? 'data' : 'update',
            ),
        ),
    );

const loadBaseGeoJsonOnMapSourceUpdateMissed: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(mapSceneDataSourceUpdateMissed)),
        mergeMap((action) =>
            loadSourceData(
                action.payload.dataSource,
                'base',
            ),
        ),
    );

const loadBaseOnResetEvent: Epic = (action$, state$: StateObservable<RootState>) =>
    action$.pipe(
        filter(isActionOf(viewerWebsocketServiceResetEventMessage)),
        map((action) => mapSceneDataLoadDataSource(state$.value.mapScene.data.sources[action.payload.dataSource])),
    );

const loadMetadataWhenMetadataReceived: Epic = (action$, state$: StateObservable<RootState>) =>
    action$.pipe(
        filter(isActionOf(handleMetadataReceivedForDataSource)),
        mergeMap(action => {
            const dataSourceId = action.payload.dataSourceId;
            const dataSourceConfig = dataSourceFactory(dataSourceId);
            if (dataSourceConfig && dataSourceConfig.onMetadataReceived) {
                return dataSourceConfig.onMetadataReceived(action.payload.metadata, state$.value);
            }
            return EMPTY;
        }),
    );

const updateMapSourceDataOnWebsocketReceivedUpdateMessage: Epic = (action$) =>
    action$.pipe(
        filter(isActionOf(viewerWebsocketServiceMapSourceUpdateMessage)),
        map(({payload: {mapSource, payload}}) => mapSceneDataUpdateDataSourceFromWebsocket(mapSource, payload)),
    );

const mapSourceEpics: Epic = combineEpics(
    buildSearchObjectsOnReceivedSourceBaseData,
    clearFilteredDataForMapSourceOnClearMapFilteredData,
    loadBaseDataOnActivateMapSource,
    loadSourceDataOnLoadDataSource,
    activateDataSourceConfigOnLoadDataSource,
    loadSourceDataOnUpdateDataSource,
    delegateMapLayerDataStorageOnReceivedSourceData,
    loadBaseGeoJsonOnMapSourceUpdateMissed,
    loadBaseOnResetEvent,
    updateMapSourceDataOnWebsocketReceivedUpdateMessage,
    loadMetadataWhenMetadataReceived,
    loadMetadataOnReceivedDatasourceConfiguration,
);

export default mapSourceEpics;
